import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Switch,
  CircularProgress,
  Snackbar,
  Alert,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import Parse from 'parse';
import { useGetIdentity } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import CustomPagination from '../../components/CustomPagination';
import CreateEditAdminDialog from './dialog/CreateEditAdminDialog';

const AdminList = () => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchBy, setSearchBy] = useState('name');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef(null);
  const navigate = useNavigate();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();

  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');
  const [toggleLoadingId, setToggleLoadingId] = useState(null);

  // key -> label for the granted-sections column. Comes from the server so a
  // new tab labels itself here without a frontend change.
  const [sectionLabels, setSectionLabels] = useState({});

  useEffect(() => {
    let cancelled = false;
    Parse.Cloud.run('getAdminSections')
      .then((result) => {
        if (cancelled) return;
        const labels = {};
        (result?.sections || []).forEach((section) => {
          labels[section.key] = section.label;
        });
        setSectionLabels(labels);
      })
      .catch(() => {
        if (!cancelled) setSectionLabels({});
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchValue]);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Parse.Cloud.run('getAllAdmins', {
        limit: perPage,
        skip: (page - 1) * perPage,
        search: debouncedSearch || undefined
      });

      if (result.success) {
        setAdmins(result.admins || []);
        setTotal(result.total || 0);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to fetch admins',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, debouncedSearch]);

  useEffect(() => {
    if (identityLoading) return;

    if (identity?.role !== 'Super-User') {
      navigate('/operatorGames', { replace: true });
      return;
    }

    fetchAdmins();
  }, [fetchAdmins, identity, identityLoading, navigate]);

  const handleExport = async () => {
    try {
      const result = await Parse.Cloud.run('getAllAdmins', {
        limit: 10000,
        skip: 0,
        search: searchValue || undefined
      });

      if (!result.success || !result.admins || result.admins.length === 0) {
        setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }

      const exportData = result.admins.map((admin) => ({
        Name: admin.name,
        Username: admin.username,
        Email: admin.email,
        Phone: admin.phone || '',
        Role: admin.role,
        Status: admin.isActive ? 'Active' : 'Inactive',
        'Created At': admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Admins');
      XLSX.writeFile(wb, `admins_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({ open: true, message: 'Failed to export data', severity: 'error' });
    }
  };

  const handleCreateClick = () => {
    setSelectedAdmin(null);
    setDialogMode('create');
    setCreateEditDialogOpen(true);
  };

  const handleEditClick = (admin) => {
    setSelectedAdmin(admin);
    setDialogMode('edit');
    setCreateEditDialogOpen(true);
  };

  const handleAdminSaved = async () => {
    setSnackbar({
      open: true,
      message:
        dialogMode === 'create' ? 'Admin created successfully' : 'Admin updated successfully',
      severity: 'success'
    });
    fetchAdmins();
  };

  const handleToggleStatus = async (admin) => {
    const nextStatus = admin.isActive === false;
    const previousAdmins = admins;
    setAdmins((prev) =>
      prev.map((item) =>
        item.id === admin.id
          ? {
              ...item,
              isActive: nextStatus
            }
          : item
      )
    );
    setToggleLoadingId(admin.id);
    try {
      await Parse.Cloud.run('toggleAdminStatus', {
        adminId: admin.id
      });
      setSnackbar({
        open: true,
        message: `Admin ${nextStatus ? 'activated' : 'deactivated'} successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling admin status:', error);
      setAdmins(previousAdmins);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to update admin status',
        severity: 'error'
      });
    } finally {
      setToggleLoadingId(null);
    }
  };

  const handleClearFilters = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setSearchBy('name');
    setPage(1);
  };

  const handleSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setDebouncedSearch(searchValue);
    setPage(1);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          p: 2,
          mb: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Filter By
            </Typography>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Search By</InputLabel>
              <Select
                value={searchBy}
                label="Search By"
                onChange={(e) => setSearchBy(e.target.value)}
                sx={{
                  backgroundColor: '#2B2B2B',
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#fff'
                  }
                }}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="username">Username</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>

            <MuiTextField
              placeholder={`Enter ${searchBy}`}
              variant="outlined"
              size="small"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{
                minWidth: '200px',
                backgroundColor: '#2B2B2B',
                '& .MuiOutlinedInput-root': {
                  color: '#fff'
                }
              }}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: '#FFC107',
                color: '#000',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#FFD54F'
                }
              }}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              onClick={handleClearFilters}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#fff',
                textTransform: 'uppercase',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              Clear Filters
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleExport}
              startIcon={<FileDownloadIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#fff',
                textTransform: 'uppercase'
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateClick}
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#FFC107',
                color: '#000',
                textTransform: 'uppercase',
                '&:hover': {
                  backgroundColor: '#FFD54F'
                }
              }}
            >
              Create Admin
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              {[
                'Name',
                'Username',
                'Email',
                'Phone',
                'Sections',
                'Status',
                'Created At',
                'Actions'
              ].map((header) => (
                <Box
                  component="th"
                  key={header}
                  sx={{
                    p: 2,
                    textAlign: header === 'Actions' ? 'center' : 'left',
                    color: '#FFC107',
                    fontWeight: 600,
                    borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  {header}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {loading ? (
              <Box component="tr">
                <Box component="td" colSpan={8} sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#FFC107' }} />
                </Box>
              </Box>
            ) : admins.length === 0 ? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={8}
                  sx={{ p: 4, textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}
                >
                  No admins found
                </Box>
              </Box>
            ) : (
              admins.map((admin) => (
                <Box
                  component="tr"
                  key={admin.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {admin.name}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {admin.username}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {admin.email}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {admin.phone || '-'}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                      maxWidth: '260px'
                    }}
                  >
                    {Array.isArray(admin.allowedSections) && admin.allowedSections.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {admin.allowedSections.map((key) => (
                          <Chip
                            key={key}
                            label={sectionLabels[key] || key}
                            size="small"
                            sx={{
                              height: '20px',
                              fontSize: '11px',
                              backgroundColor: 'rgba(255,193,7,0.15)',
                              color: '#FFC107'
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                        {admin.allowedSections ? 'Profile only' : 'Default'}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={admin.isActive !== false}
                        onChange={() => handleToggleStatus(admin)}
                        disabled={toggleLoadingId === admin.id}
                        color="warning"
                      />
                    </Box>
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {admin.createdAt ? new Date(admin.createdAt).toLocaleString() : '-'}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <IconButton
                      onClick={() => handleEditClick(admin)}
                      size="small"
                      sx={{ color: '#FFC107', mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <CustomPagination
          page={page}
          perPage={perPage}
          total={total}
          setPage={setPage}
          setPerPage={setPerPage}
        />
      </Box>

      <CreateEditAdminDialog
        open={createEditDialogOpen}
        onClose={() => setCreateEditDialogOpen(false)}
        onSave={handleAdminSaved}
        admin={selectedAdmin}
        mode={dialogMode}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminList;
